import { useEffect, useState } from "react";
import { CurrencyDollar, Info, Sparkle } from "@phosphor-icons/react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { costEstimate } from "../lib/api";
import { formatMoney } from "../lib/currency";

/**
 * Compact cost estimator pill for the results header.
 *
 * Shows the total in $ up front; click for an itemised breakdown popover.
 * Estimate only — assumes Sonnet 4.5 for copy + Nano Banana for images.
 */
export default function CostEstimateBadge({ adSetId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!adSetId) return;
    let cancelled = false;
    setLoading(true);
    costEstimate(adSetId)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [adSetId]);

  if (!adSetId) return null;

  const ccy = data?.display_currency || "USD";
  const total = data?.total_display ?? data?.total_usd ?? 0;
  const formatted = total < 0.01 && total > 0 ? `< ${formatMoney(0.01, ccy)}` : formatMoney(total, ccy);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid="cost-estimate-badge"
          className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider px-3 py-2 border border-white/10 text-zinc-400 hover:text-[#ccff00] hover:border-[#ccff00]/40 transition-colors"
          title="Estimated LLM cost for this generation"
        >
          <CurrencyDollar size={13} weight="fill" />
          {loading ? (
            <Sparkle size={11} weight="fill" className="animate-spin" />
          ) : (
            <span data-testid="cost-total">{formatted}</span>
          )}
          <Info size={10} className="opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="bg-[#0a0a0a] border border-white/10 rounded-none w-80 p-0"
        align="end"
        data-testid="cost-estimate-popover"
      >
        <div className="bg-gradient-to-br from-[#ccff00]/10 via-transparent to-pink-500/5 border-b border-white/10 px-4 py-3">
          <div className="label-mono">this generation cost you</div>
          <div className="font-display text-3xl font-black tracking-tighter text-[#ccff00] mt-0.5">
            {formatted}
          </div>
        </div>

        {data && (
          <div className="p-4 space-y-3">
            <Line
              label="Ad copy"
              detail={`${data.copy.variants} variants · ${formatMoney(data.unit_prices.per_variant_usd, "USD", 4)}/ea`}
              usd={data.copy.display ?? data.copy.usd}
              ccy={ccy}
              testid="cost-line-copy"
            />
            <Line
              label="Images"
              detail={
                data.images.creatives
                  ? `${data.images.creatives} creatives · ${formatMoney(data.unit_prices.per_image_usd, "USD", 4)}/ea`
                  : "none — checkbox was off"
              }
              usd={data.images.display ?? data.images.usd}
              ccy={ccy}
              testid="cost-line-images"
            />
            <Line
              label="Compliance"
              detail={
                data.compliance.variants_checked
                  ? `${data.compliance.variants_checked} variants checked`
                  : "no policy attached"
              }
              usd={data.compliance.display ?? data.compliance.usd}
              ccy={ccy}
              testid="cost-line-compliance"
            />
            <div className="pt-2 border-t border-white/10 flex items-center justify-between">
              <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-400">
                total
              </span>
              <span className="font-display font-black text-white">
                {formatted}
              </span>
            </div>
            <div className="text-[10px] font-mono text-zinc-600 leading-relaxed pt-2 border-t border-white/5">
              Estimate ±20%
              {ccy !== "USD" && ` · billed in USD, shown in ${ccy}`}. Clicks &amp; analytics = <span className="text-emerald-400">{formatMoney(0, ccy, 0)}</span>.
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

const Line = ({ label, detail, usd, ccy, testid }) => (
  <div className="flex items-start justify-between gap-3" data-testid={testid}>
    <div className="min-w-0 flex-1">
      <div className="font-mono text-[11px] text-zinc-300">{label}</div>
      <div className="font-mono text-[10px] text-zinc-500 mt-0.5">{detail}</div>
    </div>
    <div className="font-mono text-xs text-white font-bold flex-shrink-0">
      {formatMoney(usd, ccy, 4)}
    </div>
  </div>
);
